import { handleAddToCollectionOp } from "@sg/query";
import { useMutation, useQuery } from "react-query";
import { useParams } from "react-router-dom";
import { z } from "zod";
import useAuthStore from "~/store/auth";
import { AuditableSchema } from "~/types";
import { agent } from "~/util/agent";
import { useIsSuperAdmin } from "../util/useRoles";

const OrgBaseSchema = z.object({
  display_name: z.string().trim().min(1),
});

const OrgCreateSchema = OrgBaseSchema.extend({
  admin: z.object({
    first_name: z.string().trim().min(1),
    last_name: z.string().trim().min(1),
    email: z.string().email().min(1),
  }),
});

const OrgSchema = OrgBaseSchema.extend({
  domain_name: z.string().trim().min(1),
  external_id: z.string().trim().min(1),
}).merge(AuditableSchema);

type OrgResourceType = z.infer<typeof OrgSchema>;
type CreateOrgInput = Omit<z.infer<typeof OrgCreateSchema>, "external_id">;

const QUERY_ORGS_KEY = "organizations";

const useOrgs = () => {
  const isSuperAdmin = useIsSuperAdmin();
  return useQuery([QUERY_ORGS_KEY], async () => agent("orgs/").json<OrgResourceType[]>(), {
    enabled: isSuperAdmin,
  });
};

const useOrg = (id?: string, skip?: boolean) =>
  useQuery([QUERY_ORGS_KEY, id], async () => agent(`orgs/${id}`).json<OrgResourceType>(), { enabled: !!id && !skip });

const useCurrentOrg = () => {
  const userInfo = useAuthStore((state) => state.userInfo);
  const authToken = useAuthStore((state) => state.authToken);
  const { orgId } = useParams();
  return useOrg(orgId || userInfo.org_id, !authToken);
};

// Wrap as a mutation, used to fetch org data on demand.
const useOrgOnDemand = () => {
  return useMutation<OrgResourceType, Error, { org_id: string }>(({ org_id }) => {
    return agent(`orgs/${org_id}`).json<OrgResourceType>();
  });
};

const useCreateOrg = () => {
  return useMutation<unknown, Error, CreateOrgInput>(
    (data) => {
      return agent.post("orgs/", { labels: ["created", "org"], json: data });
    },
    { ...handleAddToCollectionOp(QUERY_ORGS_KEY) }
  );
};

export { OrgCreateSchema, QUERY_ORGS_KEY, useCreateOrg, useCurrentOrg, useOrg, useOrgOnDemand, useOrgs };
export type { CreateOrgInput, OrgResourceType };
